import { applyMiddleware, compose, createStore } from "redux";
import type { Reducer, Store } from "redux";
import { createLogger } from "redux-logger";
import { thunk as thunkMiddleware } from "redux-thunk";
import type { ThunkDispatch } from "redux-thunk";
import type { UnknownAction } from "redux";
import rootReducer from "reducers";
import { isProduction } from "utils/determineSite";
import type { RootState } from "types/state";

export type { RootState } from "types/state";
export type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

const loggerMiddleware = createLogger();
export default function configureStore(
  initialState: Partial<RootState> = {}
): Store<RootState> {
  // add redux dev tools if we are running in development
  const composeEnhancers =
    !isProduction() && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const store = createStore(
    // All reducers are TS as of Phase 4 batch 4, so rootReducer's inferred
    // shape is now real. RootState (types/state.ts) stays the declared source
    // of truth — it carries the curated index signatures and docs the inferred
    // combined shape would lose — so we still cast the root reducer to it.
    rootReducer as unknown as Reducer<
      RootState,
      UnknownAction,
      Partial<RootState>
    >,
    initialState,
    composeEnhancers(applyMiddleware(thunkMiddleware, loggerMiddleware))
  );

  if (!isProduction()) {
    store.subscribe(() => {
      localStorage.setItem("reduxState", JSON.stringify(store.getState()));
    });
  }

  // Vite hot module replacement for reducers. `import.meta.hot` is undefined in
  // production builds, so this block tree-shakes away. Replaces webpack's
  // `module.hot.accept("reducers", …)` + CJS `require("reducers/index")`.
  if (import.meta.hot) {
    import.meta.hot.accept("reducers", (nextModule) => {
      if (nextModule) {
        store.replaceReducer(
          nextModule.default as Reducer<RootState, UnknownAction, RootState>
        );
      }
    });
  }

  return store;
}
